import {
  CancelRefundAction,
  CancelRefundItem,
  CancelRefundItemChoice,
  CancelRefundOptions,
  CancelRefundReason,
  CancelRefundReasonPrompt,
  CancelRefundRequest,
  CancelTiming,
  RefundOption,
} from '../../../services/cancel-refund.service';

/**
 * CWS-1778: the panel's arithmetic and rules, kept pure so the component is
 * only wiring. Everything here mirrors a rule in svc-order
 * (itemStateEngine / refundCalculator / cancellationExecuteService) — the
 * server re-checks all of it; this is what lets the panel grey Confirm
 * before a round trip rather than after.
 *
 * The design (Orders Refund Flow, 19 Oct) opens the panel for ONE thing at a
 * time: "Refund add-on" is the add-on alone, "Cancel membership" the
 * membership alone, and "Refund single wash + add-on" the two completed
 * lines together as one combined line ("Ultimate Wash + Tire Shine · $36").
 * So the target is decided by the menu item tapped, never ticked in the panel.
 */

/** Which menu item opened the panel; decides the target items and the title. */
export type CancelRefundIntent = 'cancel' | 'refund';

export interface ItemChoice {
  option: RefundOption;
  /** What the operator typed for a custom refund, in dollars, as typed. */
  customInput: string;
}

export interface SelectionState {
  choice: ItemChoice;
  reasonCode: string | null;
  reasonNote: string;
  /** Cash only: the operator confirms the money changed hands. */
  cashHandedOver: boolean;
  /** Membership only: the design's "When". */
  cancelTiming: CancelTiming;
}

export const REASON_REQUIRING_NOTE = 'other';
export const REASON_NOTE_MAX = 500;

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function formatCents(cents: number | null | undefined): string {
  return USD.format(Math.max(0, Number(cents ?? 0)) / 100);
}

export function actionable(items: CancelRefundItem[]): CancelRefundItem[] {
  return items.filter(item => item.action === 'cancel' || item.action === 'refund');
}

/**
 * The items the panel acts on: the ones that match the menu item tapped.
 *
 * Nothing matching the intent (an older row, or a stale menu) yields an empty
 * list, and the panel then explains itself through `unavailableCopy` — which is
 * careful to say the order can still be refunded when it can.
 */
export function targetItems(
  items: CancelRefundItem[],
  intent: CancelRefundIntent
): CancelRefundItem[] {
  // Fail closed: a "Refund" that matches no refundable line must not turn into a cancel of
  // the live line under the same button. The panel then reads "nothing to refund".
  return actionable(items).filter(item => item.action === intent);
}

/** Mirrors itemStateEngine.resolveOrderAction for the target items. */
export function actionFor(items: CancelRefundItem[]): CancelRefundAction {
  const hasCancel = items.some(item => item.action === 'cancel');
  const hasRefund = items.some(item => item.action === 'refund');
  if (hasCancel && hasRefund) return 'mixed';
  if (hasCancel) return 'cancel';
  if (hasRefund) return 'refund';
  return 'none';
}

/** "Ultimate Wash + Tire Shine" */
export function targetLabel(items: CancelRefundItem[]): string {
  return items.map(item => item.label).join(' + ');
}

/**
 * The refund options the target offers as one: a single item's own list; for
 * a combined line, what every line offers (prorated never combines).
 */
export function targetOptions(items: CancelRefundItem[]): RefundOption[] {
  if (items.length === 0) return [];
  if (items.length === 1) return items[0].refund_options || [];
  const shared = items
    .map(item => new Set(item.refund_options || []))
    .reduce((acc, set) => new Set([...acc].filter(option => set.has(option))));
  return (['none', 'full', 'custom'] as RefundOption[]).filter(option => shared.has(option));
}

export interface CustomBounds {
  min_cents: number;
  max_cents: number;
}

/** What each option would give back for the target, capped at the order's refundable total. */
export function targetAmounts(
  items: CancelRefundItem[],
  orderRefundableCents: number
): { none: number; prorated: number | null; full: number; custom: CustomBounds } {
  const cap = Math.max(0, orderRefundableCents);
  const sum = (pick: (item: CancelRefundItem) => number) =>
    Math.min(
      cap,
      items.reduce((total, item) => total + Math.max(0, pick(item)), 0)
    );
  const full = sum(item => item.refund_amounts?.full ?? 0);
  const customMax = sum(item => item.refund_amounts?.custom?.max_cents ?? 0);
  const prorated =
    items.length === 1 && items[0].refund_amounts?.prorated != null
      ? Math.min(cap, items[0].refund_amounts.prorated)
      : null;
  return {
    none: 0,
    prorated,
    full,
    custom: { min_cents: customMax >= 1 ? 1 : 0, max_cents: customMax },
  };
}

/**
 * A completed item defaults to its full refund; a live item defaults to no
 * refund, because a cancellation is often "stop billing" with nothing owed,
 * and giving money back should be a deliberate pick.
 */
export function defaultOption(items: CancelRefundItem[]): RefundOption {
  const offered = targetOptions(items);
  if (actionFor(items) === 'refund' && offered.includes('full')) return 'full';
  if (offered.includes('none')) return 'none';
  return offered[0] ?? 'none';
}

/** The reason list that goes with the target's action. */
export function reasonsFor(
  options: CancelRefundOptions,
  action: CancelRefundAction
): CancelRefundReason[] {
  const key = action === 'refund' ? 'refund' : 'cancel';
  return options.reasons_by_action?.[key] ?? options.reasons ?? [];
}

export function promptFor(
  options: CancelRefundOptions,
  action: CancelRefundAction
): CancelRefundReasonPrompt {
  const key = action === 'refund' ? 'refund' : 'cancel';
  return (
    options.reason_prompts?.[key] ??
    options.reason_prompt ?? {
      title: action === 'refund' ? 'Refund reason' : 'Why is the customer cancelling?',
      helper: '',
    }
  );
}

/** "$12.00" typed by the operator → 1200; null when it is not a money amount. */
export function parseDollarsToCents(input: string): number | null {
  const cleaned = String(input ?? '')
    .replace(/[$,\s]/g, '')
    .trim();
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

export interface ChoiceProblem {
  code: 'custom_required' | 'custom_too_small' | 'custom_too_large';
  message: string;
}

/** What the choice would refund for the target, or the problem with it. */
export function amountForChoice(
  items: CancelRefundItem[],
  orderRefundableCents: number,
  choice: ItemChoice
): { cents: number; problem: ChoiceProblem | null } {
  const amounts = targetAmounts(items, orderRefundableCents);
  if (choice.option === 'custom') {
    const cents = parseDollarsToCents(choice.customInput);
    if (cents === null) {
      return { cents: 0, problem: { code: 'custom_required', message: 'Enter an amount' } };
    }
    if (cents < Math.max(1, amounts.custom.min_cents)) {
      return {
        cents,
        problem: { code: 'custom_too_small', message: 'A custom refund must be more than $0' },
      };
    }
    if (cents > amounts.custom.max_cents) {
      return {
        cents,
        problem: {
          code: 'custom_too_large',
          message: `Max ${formatCents(amounts.custom.max_cents)}`,
        },
      };
    }
    return { cents, problem: null };
  }
  if (choice.option === 'prorated') return { cents: amounts.prorated ?? 0, problem: null };
  if (choice.option === 'full') return { cents: amounts.full, problem: null };
  return { cents: 0, problem: null };
}

export interface ValidationResult {
  ok: boolean;
  /** Why Confirm is greyed; the first blocking problem. */
  reason: string | null;
}

export function validateSelection(
  options: CancelRefundOptions,
  items: CancelRefundItem[],
  state: SelectionState
): ValidationResult {
  const action = actionFor(items);
  if (action === 'none') return { ok: false, reason: 'Nothing on this order can be actioned' };

  // CWS-1846: a reload can withdraw the option that is still selected.
  if (!targetOptions(items).includes(state.choice.option)) {
    return { ok: false, reason: 'That refund option is no longer offered' };
  }
  const { cents, problem } = amountForChoice(items, options.refundable_cents, state.choice);
  if (problem) return { ok: false, reason: problem.message };

  if (!state.reasonCode || !reasonsFor(options, action).some(r => r.code === state.reasonCode)) {
    return { ok: false, reason: 'Choose a reason' };
  }
  if (state.reasonCode === REASON_REQUIRING_NOTE && !state.reasonNote.trim()) {
    return { ok: false, reason: 'Say what the reason is' };
  }
  if (state.reasonNote.length > REASON_NOTE_MAX) {
    return { ok: false, reason: `Keep the note under ${REASON_NOTE_MAX} characters` };
  }
  if (cents > 0 && options.refund_method === 'cash' && !state.cashHandedOver) {
    return { ok: false, reason: 'Confirm the cash was handed back' };
  }
  return { ok: true, reason: null };
}

/**
 * A custom amount typed against a combined line is split across its lines in
 * order, each taking up to its own maximum — the same allocation the server
 * applies when it caps a refund against the live charge.
 */
function allocateCustom(items: CancelRefundItem[], cents: number): number[] {
  let left = cents;
  return items.map(item => {
    const max = Math.max(0, item.refund_amounts?.custom?.max_cents ?? 0);
    const take = Math.min(max, left);
    left -= take;
    return take;
  });
}

export function buildRequest(
  options: CancelRefundOptions,
  items: CancelRefundItem[],
  state: SelectionState,
  requestId: string,
  target: {
    locationId?: string | null;
    subscriptionId?: string | null;
    orderId?: string | null;
    /** CWS-1947: the window this Confirm was started inside, when there is one. */
    holdId?: string | null;
  }
): CancelRefundRequest {
  const { option } = state.choice;
  const custom =
    option === 'custom'
      ? allocateCustom(items, amountForChoice(items, options.refundable_cents, state.choice).cents)
      : [];
  const lines: CancelRefundItemChoice[] = items.map((item, index) => {
    if (option !== 'custom') return { item_id: item.item_id, refund_option: option };
    // A line that gets nothing from the split is sent as no refund; the server
    // refuses a $0 custom amount.
    return custom[index] > 0
      ? { item_id: item.item_id, refund_option: 'custom', custom_amount_cents: custom[index] }
      : { item_id: item.item_id, refund_option: 'none' };
  });

  const body: CancelRefundRequest = {
    request_id: requestId,
    version: options.version,
    reason_code: state.reasonCode ?? '',
    items: lines,
  };
  if (target.locationId) body.location_id = target.locationId;
  if (target.holdId) body.hold_id = target.holdId;
  // Only a live membership has a "when"; the server ignores it otherwise.
  if (
    options.membership &&
    items.some(item => item.kind === 'package' && item.action === 'cancel')
  ) {
    body.cancel_timing = state.cancelTiming;
  }
  // Opened from a membership (Customers tab, lane pop-up) the panel names the
  // subscription; opened from an order row it names the order. Never both.
  if (!target.orderId && target.subscriptionId) {
    body.subscription_id = target.subscriptionId;
  } else {
    body.order_id = options.order_id;
  }
  const note = state.reasonNote.trim();
  if (note) body.reason_note = note;
  return body;
}

/** "18 unused days" / "2 unused washes" under a prorated figure. */
export function proratedBasisLabel(item: CancelRefundItem | undefined): string | null {
  const basis = item?.refund_basis?.prorated;
  if (!basis) return null;
  const unit = basis.kind === 'days' ? ['day', 'days'] : ['wash', 'washes'];
  return `${basis.remaining} unused ${basis.remaining === 1 ? unit[0] : unit[1]}`;
}

/** "Visa ··4242", "card", "cash" — the design's "Total refund to …" line. */
export function refundDestinationLabel(options: CancelRefundOptions): string {
  if (options.refund_method === 'cash') return 'cash';
  const card = options.refund_destination;
  if (card?.last4) {
    const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'Card';
    return `${brand} ··${card.last4}`;
  }
  return 'card';
}

export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function newRequestId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === 'x' ? r : (r % 4) + 8;
    return v.toString(16);
  });
}

/** Why an item on the order is greyed, by the server's reason code. */
export const ITEM_REFUSAL_COPY: Record<string, string> = {
  fully_refunded: 'Already refunded',
  already_cancelled: 'Already cancelled',
  cancel_already_scheduled: 'Cancellation already scheduled',
  imported_membership: 'Managed by the vendor',
  family_plan: 'Billed to the primary member',
  rewash: 'A rewash cost nothing',
  not_paid: 'Nothing was paid',
  unsupported_purchase_type: 'Not available for this purchase',
};

/**
 * CWS-1947: seconds left on the hold, from the server's deadline.
 *
 * Counted from `expires_at` rather than ticked down locally, so a tab that was
 * backgrounded, a sleeping tablet or a slow render cannot leave the panel
 * showing time the server will not honour.
 */
export function holdRemainingSeconds(
  expiresAt: string | null | undefined,
  now: number = Date.now()
): number {
  if (!expiresAt) return 0;
  const deadline = new Date(expiresAt).getTime();
  if (Number.isNaN(deadline)) return 0;
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

/** "0:58" — the countdown as the operator reads it. */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

/**
 * CWS-1947: why the panel cannot act, worded truthfully.
 *
 * The bug this replaces: when the menu said Cancel but the order had completed
 * in the meantime, every line came back actionable as `refund`, the intent
 * filter emptied the list, and the panel announced that nothing could be
 * cancelled OR refunded. The order was refundable the whole time, and the
 * operator was left with no way forward from either button.
 *
 * So the empty-list case is split: an order that IS actionable, just not for the
 * button that was pressed, says so and names the way out. Only an order with
 * nothing actionable at all keeps the old wording.
 */
export function unavailableCopy(options: CancelRefundOptions, intent: CancelRefundIntent): string {
  const items = options.items || [];
  const actionableItems = actionable(items);

  if (actionableItems.length > 0) {
    return intent === 'cancel'
      ? 'This order has already been completed, so it can no longer be cancelled — it can still be refunded. Close this and choose Refund.'
      : 'Nothing on this order has been completed yet, so there is nothing to refund — it can still be cancelled. Close this and choose Cancel.';
  }

  const reason = items.find(item => item.reason)?.reason ?? '';
  return ITEM_REFUSAL_COPY[reason]
    ? `${ITEM_REFUSAL_COPY[reason]} — nothing on this order can be cancelled or refunded.`
    : 'Nothing on this order can be cancelled or refunded.';
}

/** Stable API codes → attendant copy. Anything else falls back to the server message. */
export const ERROR_COPY: Record<string, string> = {
  platform_disabled: 'Cancel and refund are not switched on for this environment.',
  location_disabled: 'Cancel and refund are not switched on at this location.',
  not_found: 'This order could not be found.',
  membership_not_found: 'No membership was found behind this order.',
  changed_refresh:
    'This order changed since it was opened. The amounts have been updated, so check them and confirm again.',
  item_not_actionable: 'One of the items can no longer be actioned. Close and open it again.',
  nothing_to_do: 'Nothing on this order can be actioned.',
  // CWS-1947: the cancellation window.
  hold_expired:
    'The cancellation window has closed. Close this and open Cancel again to start over.',
  held_elsewhere: 'Someone else is cancelling this order right now.',
  nothing_to_hold: 'This order can no longer be cancelled.',
  nothing_refundable: 'There is nothing left to refund on this order.',
  nothing_to_refund: 'Choose an amount to refund.',
  no_refundable_charge: 'No card payment was found to refund against.',
  refund_option_unavailable: 'That refund option is no longer offered. Close and open it again.',
  custom_amount_too_large: 'The custom amount is more than what can be refunded.',
  custom_amount_too_small: 'A custom refund must be more than $0.',
  invalid_custom_amount: 'Enter a valid custom amount.',
  billing_stop_failed: 'The membership billing could not be stopped. Nothing was changed.',
  cancellation_failed: 'The cancellation could not be completed. Nothing was changed.',
  subscription_mismatch:
    'The membership behind this order does not match the one on file. Nothing was changed.',
  invalid_reason_code: 'Choose a reason from the list.',
  reason_note_required: 'Say what the reason is.',
  // CWS-1579: family plan cancellation.
  FAMILY_MODE_REQUIRED: 'This is the family primary. Choose Cancel all or Cancel primary only.',
  FAMILY_MODE_INVALID: 'Only the primary of a family can be cancelled this way.',
  FAMILY_NOT_FOUND: 'This membership is no longer in a family. Close and open it again.',
  FAMILY_NEW_PRIMARY_REQUIRED: 'Choose which member becomes the primary.',
  FAMILY_NEW_PRIMARY_INVALID: 'The new primary must be one of the plates staying in the family.',
  FAMILY_NEW_PRIMARY_ENDING: "That plate's membership is ending, so it can't become the primary.",
  FAMILY_NO_PRIMARY_CANDIDATE: 'Every other plate is already ending. Use Cancel all plates.',
  FAMILY_PRIMARY_MISSING: 'This family has no primary. Contact support to fix it.',
  FAMILY_PLATES_INCOMPLETE: 'The family changed since it was opened. Close and open it again.',
  FAMILY_PLATES_INVALID: 'The family changed since it was opened. Close and open it again.',
  FAMILY_PACKAGE_REQUIRED: 'Every plate must cancel its membership.',
};

/**
 * CWS-1846: whether a reloaded options answer offers the operator the same thing the panel
 * already shows. The server's version token also turns over for changes the panel never
 * priced, and a Confirm that lands on one of those can be resent with the fresh token
 * without the operator noticing. Anything that moves an amount, an option or an item's
 * action is a real change, and then they must see the new figures before confirming.
 */
export function sameOffer(
  before: CancelRefundOptions,
  after: CancelRefundOptions,
  intent: CancelRefundIntent
): boolean {
  if (before.refundable_cents !== after.refundable_cents) return false;
  if (before.prorated_refund_enabled !== after.prorated_refund_enabled) return false;
  if (before.refund_method !== after.refund_method) return false;
  if (before.refund_destination?.brand !== after.refund_destination?.brand) return false;
  if (before.refund_destination?.last4 !== after.refund_destination?.last4) return false;
  if (before.membership?.current_period_end !== after.membership?.current_period_end) return false;
  if (before.prepaid?.remaining_washes !== after.prepaid?.remaining_washes) return false;
  const priced = (item: CancelRefundItem) => [
    item.item_id,
    item.kind,
    item.state,
    item.action,
    item.amount_cents,
    item.refundable_cents,
    [...item.refund_options].sort().join(','),
    item.refund_amounts.none ?? null,
    item.refund_amounts.prorated ?? null,
    item.refund_amounts.full ?? null,
    item.refund_amounts.custom?.max_cents ?? null,
  ];
  const a = targetItems(before.items, intent).map(priced);
  const b = targetItems(after.items, intent).map(priced);
  return JSON.stringify(a) === JSON.stringify(b);
}
